import * as React from 'react';

import { cn } from '../lib/utils';

export interface SwitchProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'value'> {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

/**
 * Toggle switch — shadcn/scio3 `Switch` look (h-5 w-9 track, h-4 w-4 thumb) built on a plain
 * `<button role="switch" aria-checked>` because `@radix-ui/react-switch` is not a dependency here.
 * Controlled (`checked`) or uncontrolled (`defaultChecked`); Space/Enter toggle via the native button.
 * Exposes `data-state="checked|unchecked"` like the Radix version so styling/selectors carry over.
 */
const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ className, checked, defaultChecked, onCheckedChange, onClick, disabled, ...props }, ref) => {
    const [inner, setInner] = React.useState(!!defaultChecked);
    const isControlled = checked !== undefined;
    const on = isControlled ? !!checked : inner;
    return (
      <button
        type="button"
        role="switch"
        aria-checked={on}
        data-state={on ? 'checked' : 'unchecked'}
        disabled={disabled}
        ref={ref}
        className={cn(
          'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
          className,
        )}
        onClick={(e) => {
          onClick?.(e);
          if (e.defaultPrevented) return;
          if (!isControlled) setInner(!on);
          onCheckedChange?.(!on);
        }}
        {...props}
      >
        <span
          aria-hidden
          data-state={on ? 'checked' : 'unchecked'}
          className="pointer-events-none block h-4 w-4 rounded-full bg-background shadow-sm ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
        />
      </button>
    );
  },
);
Switch.displayName = 'Switch';

export { Switch };
